import type { JsonValue } from "@e308/core";
import { BOT_DIFFICULTIES, BOT_PERSONALITIES } from "./content/setup.js";
import type {
  EconomySettings,
  MatchEventPayload,
  NewBorderEventPayload,
  SeatEventPayload,
  ShieldEventPayload,
} from "./content-types.js";
import { METAL_LANES, POWER_DEPARTMENTS } from "./economy-types.js";
import { lockMatchSetup } from "./lifecycle-state.js";
import type { ConflictConfig, LockedSeat } from "./types.js";

const SEAT_ID = /^[a-z][a-z0-9-]*$/;

export function validateConflictConfig(config: ConflictConfig): void {
  if (config.seats.length < 2) throw new TypeError("A conflict requires at least two seats");
  const ids = new Set(config.seats.map((seat) => seat.id));
  if (ids.size !== config.seats.length) throw new TypeError("Seat IDs must be unique");
  for (const seat of config.seats) validateSeat(seat, config.seats, ids);
  validateTeams(config);
  if (config.eventSetting !== undefined && !["off", "announced"].includes(config.eventSetting))
    throw new TypeError(`Unknown event setting: ${config.eventSetting}`);
  lockMatchSetup(config);
  if (config.stepMs !== undefined && (!Number.isSafeInteger(config.stepMs) || config.stepMs < 1)) {
    throw new TypeError("stepMs must be a positive safe integer");
  }
  if (config.matchId !== undefined && !SEAT_ID.test(config.matchId)) {
    throw new TypeError(`Invalid match ID: ${config.matchId}`);
  }
}

function validateTeams(config: ConflictConfig): void {
  const mode = config.mode ?? "every-town-for-itself";
  if (!["every-town-for-itself", "team-shift", "private-practice"].includes(mode))
    throw new TypeError(`Unknown match mode: ${mode}`);
  if (mode !== "team-shift") {
    if (config.seats.some((seat) => seat.teamId !== undefined))
      throw new TypeError("Teams are only valid in Team Shift");
    return;
  }
  if (config.seats.some((seat) => !seat.teamId || !SEAT_ID.test(seat.teamId)))
    throw new TypeError("Every Team Shift seat needs a valid team ID");
  const sizes = new Map<string, number>();
  for (const seat of config.seats)
    sizes.set(seat.teamId as string, (sizes.get(seat.teamId as string) ?? 0) + 1);
  if (sizes.size < 2) throw new TypeError("Team Shift requires at least two teams");
  if (new Set(sizes.values()).size !== 1)
    throw new TypeError("Team Shift requires equal team sizes");
  for (const seat of config.seats) {
    if (
      !seat.adjacentTo.some(
        (neighbor) =>
          config.seats.find((candidate) => candidate.id === neighbor)?.teamId !== seat.teamId,
      )
    )
      throw new TypeError(`Team ${seat.teamId} needs an enemy border`);
  }
  for (let index = 0; index < config.seats.length; index += 1) {
    const seat = config.seats[index] as LockedSeat;
    const next = config.seats[(index + 1) % config.seats.length] as LockedSeat;
    if (seat.teamId === next.teamId)
      throw new TypeError("Team placement must alternate around the ring");
  }
}

function validateSeat(
  seat: LockedSeat,
  seats: readonly LockedSeat[],
  ids: ReadonlySet<string>,
): void {
  if (!SEAT_ID.test(seat.id)) throw new TypeError(`Invalid seat ID: ${seat.id}`);
  if (seat.adjacentTo.length === 0) throw new TypeError(`Seat ${seat.id} needs a neighbor`);
  if (new Set(seat.adjacentTo).size !== seat.adjacentTo.length)
    throw new TypeError(`Seat ${seat.id} has duplicate neighbors`);
  if (seat.bot) {
    const personalities = [...BOT_PERSONALITIES.map((entry) => entry.id), "seeded-mix"];
    if (!personalities.includes(seat.bot.personality))
      throw new TypeError(`Unknown bot personality: ${seat.bot.personality}`);
    if (!BOT_DIFFICULTIES.some((entry) => entry.id === seat.bot?.difficulty))
      throw new TypeError(`Unknown bot difficulty: ${seat.bot.difficulty}`);
  }
  for (const targetId of seat.adjacentTo) {
    if (!ids.has(targetId) || targetId === seat.id) {
      throw new TypeError(`Invalid adjacency ${seat.id} -> ${targetId}`);
    }
    if (!seats.find((candidate) => candidate.id === targetId)?.adjacentTo.includes(seat.id)) {
      throw new TypeError(`Adjacency must be symmetric: ${seat.id} <-> ${targetId}`);
    }
  }
}

export function seatEventPayload(value: unknown): SeatEventPayload {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).length !== 1 ||
    typeof (value as { seatId?: unknown }).seatId !== "string"
  ) {
    throw new TypeError("Invalid seat event payload");
  }
  return { seatId: (value as { seatId: string }).seatId };
}

export function shieldEventPayload(value: unknown): ShieldEventPayload {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).length !== 2 ||
    typeof (value as { seatId?: unknown }).seatId !== "string" ||
    typeof (value as { borderId?: unknown }).borderId !== "string"
  )
    throw new TypeError("Invalid shield event payload");
  return value as ShieldEventPayload;
}

export function newBorderEventPayload(value: unknown): NewBorderEventPayload {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).length !== 3 ||
    typeof (value as { seatId?: unknown }).seatId !== "string" ||
    typeof (value as { borderId?: unknown }).borderId !== "string" ||
    !Number.isSafeInteger((value as { fightingStartsAtMs?: unknown }).fightingStartsAtMs)
  )
    throw new TypeError("Invalid new-border event payload");
  return value as NewBorderEventPayload;
}

export function matchEventPayload(value: unknown): MatchEventPayload {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).length !== 2
  )
    throw new TypeError("Invalid match event payload");
  const payload = value as { outcome?: unknown; winnerId?: unknown };
  if (
    !["seat-win", "team-win", "draw"].includes(payload.outcome as string) ||
    (payload.winnerId !== null && typeof payload.winnerId !== "string")
  )
    throw new TypeError("Invalid match event payload");
  return value as MatchEventPayload;
}

export function emptyEventPayload(value: unknown): JsonValue {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).length !== 0
  )
    throw new TypeError("Invalid empty event payload");
  return {};
}

export function validateEconomySettings(value: unknown): EconomySettings & JsonValue {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Invalid economy settings");
  }
  const settings = value as EconomySettings;
  validatePriority(settings.powerPriority, POWER_DEPARTMENTS);
  validatePriority(settings.metalPriority, METAL_LANES);
  return {
    powerPriority: [...settings.powerPriority],
    metalPriority: [...settings.metalPriority],
  };
}

export function validatePriority(value: unknown, expected: readonly string[]): void {
  if (
    !Array.isArray(value) ||
    value.length !== expected.length ||
    new Set(value).size !== expected.length ||
    !value.every((entry) => expected.includes(entry))
  ) {
    throw new TypeError("Priority must contain every lane exactly once");
  }
}

export function validatePercentages(
  values: Readonly<Record<string, number>>,
  limitedTotal: boolean,
): void {
  if (Object.values(values).some((value) => !Number.isInteger(value) || value < 0 || value > 100)) {
    throw new TypeError("Allocations must be whole percentages from 0 to 100");
  }
  if (limitedTotal && Object.values(values).reduce((sum, value) => sum + value, 0) > 100) {
    throw new TypeError("Allocation cannot exceed 100");
  }
}
